import { cx } from '@linaria/core';
import { useMemo } from 'react';
import { useContentEnginePrinters } from '@pm2/content-engine';
import { PM2DisplayProvider } from '@pm2/store';
import { useNotifier } from '@provider/notifier';
import { triggerPostloadedModal } from '@ui/kit';
import { printAST } from '@utils/ast';
import { preloadImage } from '@utils/common';
import { contentWrapper, componentFrameworkWrapper, withToolbarContainer, withToolbarWrapper, gridWrapper, sliderWrapper, fallbackWrapper } from './pm2-modal.style.js';
import { jsx } from 'react/jsx-runtime';

/**
 * The PM2 modal provides a custom integration on top of the default ui-kit
 * modal implementation which support for toolbars, lazy-loaded content etc.
 *
 * this utility provides various different methods to insert pm2 content
 * elements into a modal, with each method optimized for rendering the
 * particular content.
 */
function usePm2Modal() {
  const notifier = useNotifier();
  const {
    printers,
    interceptor
  } = useContentEnginePrinters();
  return useMemo(() => ({
    fromComponentFrameworkNode: function fromComponentFrameworkNode(...args) {
      return triggerModalFromComponentFrameworkNode(notifier, ...args);
    },
    fromIframe: function fromIframe(...args) {
      return triggerModalFromIframe(notifier, ...args);
    },
    fromAST: function fromAST(ast, options) {
      return triggerModalFromAST(notifier, ast, {
        printers,
        interceptor: (input, output, context) => {
          if (input.nodeType === "iframe-component" || input.nodeType === "iframe-component.component-framework") {
            // avoid wrapping iframes when they are going to be embedded
            // inside a lightbox
            return output;
          }
          return interceptor?.(input, output, context) ?? output;
        }
      }, options);
    }
  }), [notifier, printers, interceptor]);
}
function triggerModalFromIframe(notifier, element) {
  return triggerPostloadedModal(notifier, /*#__PURE__*/jsx(PM2DisplayProvider, {
    inGrid: false,
    inLibrary: false,
    inLightbox: true,
    children: /*#__PURE__*/jsx("div", {
      className: cx(contentWrapper),
      children: element
    })
  }));
}
function triggerModalFromComponentFrameworkNode(notifier, element, type, options) {
  return triggerPostloadedModal(notifier, /*#__PURE__*/jsx(PM2DisplayProvider, {
    inGrid: false,
    inLibrary: false,
    inLightbox: true,
    children: /*#__PURE__*/jsx("div", {
      className: cx(contentWrapper, componentFrameworkWrapper),
      "data-type": type.replace("component-framework.", ""),
      children: element
    })
  }), {
    disableToolbar: !options?.buttons?.length,
    shouldAutoFocusContent: false,
    theme: "component-framework",
    persistOnNavigation: type === "component-framework.i-ark",
    closeButtonLabel: "Luk opgaven (Esc)",
    ...options
  });
}
function triggerModalFromAST(notifier, ast, context, initialOptions) {
  const resolved = (async () => {
    const resolvedAST = await ast;
    if (resolvedAST.length === 1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const elementAST = resolvedAST[0];
      if (elementAST.nodeType === "image-component") {
        const imageAst = elementAST;
        const imageUrl = new URL(imageAst.imageSrc, "http://localhost");
        imageUrl.searchParams.set("h", String(Math.ceil(window.innerHeight / 30) * 30));
        const image = await preloadImage(imageUrl.href);
        const caption = imageAst.meta.caption;
        const hasCaption = caption.authorOrArtist?.value && (caption.authorOrArtist?.showInContext || caption.authorOrArtist?.showOutsideContext) || caption.caption?.value && (caption.caption?.showInContext || caption.caption?.showOutsideContext) || caption.copyright?.value && (caption.copyright?.showInContext || caption.copyright?.showOutsideContext) || caption.country?.value && (caption.country?.showInContext || caption.country?.showOutsideContext) || caption.description?.value && (caption.description?.showInContext || caption.description?.showOutsideContext) || caption.duration?.value && (caption.duration?.showInContext || caption.duration?.showOutsideContext) || caption.title?.value && (caption.title?.showInContext || caption.title?.showOutsideContext) || caption.year?.value && (caption.year?.showInContext || caption.year?.showOutsideContext);

        // Images should never becomes so large that scrolling is needed to
        // display the entire image.
        // As such we need to calculate the largest width that the image may
        // take without causing scroll on the vertical axis.
        const maxWidth = image.naturalWidth / image.naturalHeight * (window.innerHeight - (hasCaption ? 255 : 155));

        // @ts-expect-error We purposly overwrite the image src when embedding
        // within lightbox as the width of the image has been resolved here.
        imageAst.imageSrc = image.src;
        imageAst.meta.imageWidth = image.naturalWidth;
        imageAst.meta.imageHeight = image.naturalHeight;
        return {
          content: /*#__PURE__*/jsx("div", {
            className: cx(contentWrapper, withToolbarWrapper),
            style: {
              // add +70 to account for padding-inline
              width: Math.min(image.naturalWidth, maxWidth) + 70
            },
            children: /*#__PURE__*/jsx("div", {
              className: withToolbarContainer,
              children: printAST({
                ...imageAst,
                options: {
                  ...imageAst.options,
                  enableFullWidth: true
                }
              }, context.printers, context.interceptor)
            })
          }),
          options: initialOptions
        };
      }
      if (elementAST.nodeType === "iframe-component" || elementAST.nodeType === "iframe-component.component-framework") {
        return {
          content: /*#__PURE__*/jsx("div", {
            className: cx(contentWrapper),
            children: printAST(elementAST, context.printers)
          }),
          options: initialOptions
        };
      }

      // grids are allowed extra width, as we assume the content inside will
      // be made smaller in each of the inserted columns
      if (elementAST.nodeType === "grid-component") {
        return {
          content: /*#__PURE__*/jsx("div", {
            className: cx(contentWrapper, gridWrapper),
            children: printAST(elementAST, context.printers)
          }),
          options: initialOptions
        };
      }
      if (elementAST.nodeType === "slider-component") {
        return {
          content: /*#__PURE__*/jsx("div", {
            className: cx(contentWrapper, sliderWrapper),
            children: printAST(elementAST, context.printers)
          }),
          options: initialOptions
        };
      }
      if (elementAST.nodeType.startsWith("component-framework.")) {
        const resolvedOptions = await initialOptions;
        const ref = {
          current: null
        };
        return {
          content: /*#__PURE__*/jsx("div", {
            ref: ref,
            className: cx(contentWrapper, componentFrameworkWrapper),
            "data-type": elementAST.nodeType.replace("component-framework.", ""),
            children: printAST(elementAST, context.printers)
          }),
          options: {
            disableToolbar: elementAST.nodeType !== "component-framework.flashcard",
            ...resolvedOptions
          }
        };
      }
    }
    return {
      content: /*#__PURE__*/jsx("div", {
        className: cx(contentWrapper, fallbackWrapper),
        children: printAST(resolvedAST, context.printers, context.interceptor)
      }),
      options: initialOptions
    };
  })();
  resolved.then(it => it.options);
  return triggerPostloadedModal(notifier, resolved.then(({
    content
  }) => /*#__PURE__*/jsx(PM2DisplayProvider, {
    inGrid: false,
    inLibrary: false,
    inLightbox: true,
    children: content
  })), resolved.then(({
    options
  }) => options));
}

export { usePm2Modal };
//# sourceMappingURL=pm2-modal.js.map
